import { Box, Skeleton, Typography } from "@mui/material";
import { useRequestStore } from "entities/Request";
import React from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNumericFormat } from "react-number-format";
import { Link } from "react-router-dom";
import { ROUTES } from "shared/config";
import { useDebounce } from "shared/hooks";
import { useSWRConfig } from "swr";
import { useExchange } from "../api/hooks/useExchange";
import { baseApiService } from "shared/api";

export default function CurrencyExchange({ costLineId, storedExchange }) {
  const { t } = useTranslation("CostLine");
  const {
    formState: { dirtyFields },
  } = useFormContext();

  const isDirty = "amount" in dirtyFields || "currency_code" in dirtyFields;

  const localCurrency = useRequestStore(
    (state) => state.request.local_currency
  );
  const [amount, currency] = useWatch({ name: ["amount", "currency_code"] });
  const [payload, setPayload] = React.useState({
    amount: null,
    currency: null,
  });
  const [isTyping, setIsTyping] = React.useState(false);
  const [isLocalCurrencySelected, setIsLocalCurrencySelected] = React.useState(
    currency?.value === localCurrency
  );

  const {
    data: exchange,
    isLoading,
    error,
  } = useExchange({ ...payload, costLineId });

  const { mutate } = useSWRConfig();

  const exchangedAmount = useNumericFormat({
    thousandSeparator: " ",
    decimalScale: 2,
    fixedDecimalScale: true,
    value: exchange?.local_amount ?? "",
  });

  const debouncedCallback = useDebounce(
    (term) => {
      if (error) {
        // invalidating cache in case of error since swr is caching error responses
        mutate(
          [
            baseApiService.ENDPOINTS.currency,
            term.amount,
            term.currency.value,
            costLineId,
          ],
          undefined
        );
      }
      setPayload({ amount: term.amount, currency: term.currency.value });
      setIsTyping(false);
    },
    500,
    [error]
  );

  React.useEffect(() => {
    const isEmpty = amount === null || currency === null;
    const isLocalCurrency = currency?.value === localCurrency;
    setIsLocalCurrencySelected(isLocalCurrency);

    if (!isEmpty && isDirty && !isLocalCurrency) {
      setIsTyping(true);
      debouncedCallback({ amount, currency });
    } else {
      setPayload({
        amount: null,
        currency: null,
      });
      debouncedCallback.cancel();
      setIsTyping(false);
    }
  }, [amount, currency, localCurrency, storedExchange]);

  if (isLocalCurrencySelected) return null;

  const isActualRateDate = isDirty
    ? exchange?.actual_rate
    : storedExchange?.actual_rate;
  const exchangeRate = isDirty ? exchange?.rate : storedExchange?.rate ?? "";
  const exchangeRateDate = isDirty
    ? exchange?.rate_date
    : storedExchange?.rate_date ?? "";
  const exchangeAmount = isDirty
    ? exchangedAmount.value
    : storedExchange?.amount ?? "";
  const exchangeCurrency = isDirty
    ? exchange?.local_currency ?? ""
    : storedExchange?.currency ?? "";

  return (
    <Box sx={{ mt: 3 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          columnGap: "0.4rem",
          flexWrap: "wrap",
        }}
      >
        {t("currency_exchange_header")}
        {isTyping || isLoading ? (
          <Skeleton width="70px" />
        ) : (
          <strong>
            {error ? (
              <span className="text-red">{error?.response?.data?.message}</span>
            ) : (
              `${exchangeAmount} ${exchangeCurrency}`
            )}
          </strong>
        )}
      </Box>
      {error ? null : (
        <>
          <Typography component="div">
            {t("labels.conversion_rate")}
            {isTyping || isLoading ? (
              <Skeleton width="70px" sx={{ display: "inline-block" }} />
            ) : exchangeRate && exchangeCurrency ? (
              <Typography
                sx={{
                  display: "inline",
                  color: !isActualRateDate ? "#e74c3c" : undefined,
                }}
              >
                1 {currency?.value ?? ""} = {exchangeRate} {exchangeCurrency}
              </Typography>
            ) : (
              ""
            )}
            {isActualRateDate !== undefined && !isActualRateDate && (
              <Typography variant="subtitle2" sx={{ color: "#e74c3c" }}>
                {t("missing_rate_disclaimer")}
              </Typography>
            )}
          </Typography>
          <Typography component="div">
            {t("labels.conversion_rate_date")}
            {isTyping || isLoading ? (
              <Skeleton width="70px" sx={{ display: "inline-block" }} />
            ) : (
              exchangeRateDate
            )}
          </Typography>
        </>
      )}
      <Typography variant="subtitle2" gutterBottom>
        {t("currency_exchange_subheader")}
        <Link to={ROUTES.faq} className="fw-bolder">
          FAQ
        </Link>
      </Typography>
    </Box>
  );
}
